import { Suspense, lazy, useId } from 'react';
import { Box, Link, Stack, Typography, useTheme } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhoneIcon from '@mui/icons-material/Phone';

import Seo from 'components/Seo';
import { panelBorder, typographyColors } from 'theme/colors';
const LazyLeafletMap = lazy(() => import('components/LeafletMap'));

const ICON_SX = { fontSize: 18, color: 'primary.main' } as const;
const LIGHT_TEXT_COLOR = 'typography.light';

const Contacts: React.FC = () => {
  const theme = useTheme();
  const mapId = useId();

  return (
    <>
      <Seo
        title='Jyväskylän Huoltoykköset - Yhteystiedot'
        description='Jyväskylän Huoltoykköset autokorjaamo yhteystiedot (Seppälänkangas)'
        name='Jyväskylän Huoltoykköset: Yhteystiedot'
        type='website'
      />
      <Box
        sx={{
          '&': {
            color: LIGHT_TEXT_COLOR,
          },
        }}
      >
        <Stack spacing={3}>
          <Typography variant='h2'>YHTEYSTIEDOT</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 3 }} sx={{ flexWrap: 'wrap' }}>
            <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
              <LocationOnOutlinedIcon sx={ICON_SX} />
              <Typography variant='p3'>Kuormaajantie 6, 40320, Jyväskylä</Typography>
            </Stack>
            <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
              <AccessTimeIcon sx={ICON_SX} />
              <Typography variant='p3'>Ma–Pe 8:00–16:30</Typography>
            </Stack>
            <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
              <PhoneIcon sx={ICON_SX} />
              <Link href='tel:0452393591' underline='hover' color={LIGHT_TEXT_COLOR} sx={{ typography: 'p3' }}>
                045 239 3591
              </Link>
            </Stack>
          </Stack>
          <Stack spacing={2}>
            <Typography variant='p3' sx={{ fontWeight: 'bold' }}>
              Kokemus ja ammattitaito jokaisessa yksityiskohdassa!
            </Typography>
            <Typography variant='p3'>
              Ylpeydellä esittelemme teille meidän kokeneen ja ammattitaitoisen henkilöstömme. Tavoitteemme on tarjota teille
              korkealaatuisen autonhuollon palveluita, jotka perustuvat yli kymmenen vuoden kokemukseen ja ammattitaitoon.
            </Typography>
            <Typography variant='p3'>
              Huoltomme erottautuu korkealla koulutuksella ja ammattitaidolla, minkä avulla voimme tarjota teille aivan erityislaatuista
              palvelua. Työskentelymme on aina erityisen tarkkaa ja huolellista, mikä takaa teille parhaan mahdollisen lopputuloksen.
            </Typography>
            <Typography variant='p3'>
              Olemme avoinna arkisin klo 8:00–16:30 osoitteessa Kuormaajantie 6, 40320, Jyväskylä. Ota yhteyttä meihin puhelimitse{' '}
              <Link href='tel:0452393591' underline='hover' color={LIGHT_TEXT_COLOR}>
                045 239 3591
              </Link>{' '}
              ja varaa aikaa.
            </Typography>
            <Typography variant='p3'>
              Löydä meidät helposti Seppälänkankaalta! Sijaintimme tekee meistä täydellisen valinnan sekä koti- että työmatkailijoille,
              jotka etsivät korkealaatuisia autonhuollon palveluita läheltä.
            </Typography>
          </Stack>
        </Stack>
        <Box
          id={mapId}
          sx={{
            maxWidth: theme.breakpoints.values.lg,
            '& .leaflet-tile-pane': {
              filter: 'grayscale(1) invert(0.92) brightness(0.9) contrast(1.15)',
            },
            '& .leaflet-control-zoom': {
              border: 'none',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
            },
            '& .leaflet-control-zoom a': {
              backgroundColor: 'rgba(36,38,42,0.92)',
              color: LIGHT_TEXT_COLOR,
              borderColor: 'rgba(238,240,242,0.14)',
            },
            '& .leaflet-control-zoom a:hover': {
              backgroundColor: 'rgba(52,54,60,0.95)',
            },
            '& .leaflet-popup-content-wrapper': {
              backgroundColor: 'rgba(24,25,28,0.97)',
              color: LIGHT_TEXT_COLOR,
              borderRadius: '12px',
              border: panelBorder,
              boxShadow: '0 12px 30px -10px rgba(0,0,0,0.6)',
            },
            '& .leaflet-popup-content': {
              margin: '14px 16px',
            },
            '& .leaflet-popup-tip': {
              backgroundColor: 'rgba(24,25,28,0.97)',
            },
            '& .leaflet-popup-close-button': {
              color: 'rgba(238,240,242,0.55) !important',
            },
            '& .leaflet-popup-close-button:hover': {
              color: `${typographyColors.light} !important`,
            },
          }}
        >
          <Suspense fallback={<Box>Ladataan karttaa...</Box>}>
            <LazyLeafletMap />
          </Suspense>
        </Box>
        <Typography variant='p3'>
          Odottamme sinua <b>Jyväskylän Huoltoykkösten</b> korjaamossa Seppälänkankaalla.
        </Typography>
      </Box>
    </>
  );
};

export default Contacts;
